// src/components/ListViewWorkspace/listviewWorkspace.js

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './listviewWorkspace.css';
import useResponsiveDesign from '../components/appComponents/useResponsiveDesign';

// FIREBASE
import { collection, onSnapshot, doc, setDoc } from 'firebase/firestore';
import { storage, db, auth } from '../firebase';
import { ref, getDownloadURL } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';

// UI & IMAGES
import ProcessingPage from '../unAuthPages/ProcessingPage';
import placeholder_color_thumbnail from '../assets/images/thumbnail/no-content-bumpups.jpg';
import workspace_draft_icon from '../assets/images/icons/workspace-draft-icon.png';
import comment_icon from '../assets/images/icons/comment-icon.png';
import upload_small_icon from '../assets/images/icons/upload-small-icon.png';
import warning_icon from '../assets/images/icons/warning-icon.png';

// FONT AWESOME
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faArchive, faCog, faComment } from '@fortawesome/free-solid-svg-icons';

const ListViewWorkspace = () => {
  const [videos, setVideos] = useState([]);
  const [isContentAvailable, setIsContentAvailable] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [draftCount, setDraftCount] = useState(0);
  const [selectedVideos, setSelectedVideos] = useState(new Set());
  const videosPerPage = 15;
  const navigate = useNavigate();
  const device = useResponsiveDesign();

  // FUNCTIONS
  const functions = getFunctions();
  const deleteAssetsCallable = httpsCallable(functions, 'deleteAssets');

  const getLocalDraftCountKey = userId => `draftCount_${userId}`;
  const getLocalVideoCacheKey = userId => `videoCache_${userId}_WorkspaceVideos`;

  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingDeleteIds, setPendingDeleteIds] = useState([]);

  const confirmDelete = () => {
    setPendingDeleteIds(Array.from(selectedVideos));
    setShowConfirm(true);
  };
  const onCancelDeletion = () => {
    setShowConfirm(false);
    setPendingDeleteIds([]);
  };
  const onConfirmDeletion = () => {
    setShowConfirm(false);
    handleBulkDelete(pendingDeleteIds);
    setPendingDeleteIds([]);
  };

  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const handleBulkDelete = async () => {
    setIsBulkDeleting(true);
    const assetsList = Array.from(selectedVideos).map(id => `workspace-${id}`);
    try {
      if (deleteAssetsCallable.length === 1) {
        await deleteAssetsCallable({ assetsList });
      } else {
        await Promise.all(
          assetsList.map(single => deleteAssetsCallable({ assetsList: [single] }))
        );
      }
      const user = auth.currentUser;
      if (user) {
        const cacheKey = getLocalVideoCacheKey(user.uid);
        const raw = localStorage.getItem(cacheKey);
        if (raw) {
          const cachedVideos = JSON.parse(raw);
          const filtered = cachedVideos.filter(
            vid => !assetsList.includes(`workspace-${vid.id}`)
          );
          localStorage.setItem(cacheKey, JSON.stringify(filtered));
          setVideos(filtered);
        }
      }
      setSelectedVideos(new Set());
    } catch (err) {
      console.error('Error deleting assets:', err);
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handleSelectAllVisible = () => {
    setSelectedVideos(prev => {
      const next = new Set(prev);
      const allVisible = currentVideos.every(v => next.has(v.id));
      currentVideos.forEach(v =>
        allVisible ? next.delete(v.id) : next.add(v.id)
      );
      return next;
    });
  };

  const checkFirestore = useCallback(userId => {
    const localDraftCountKey = getLocalDraftCountKey(userId);
    const localVideoCacheKey = getLocalVideoCacheKey(userId);

    const cachedDraftCount = localStorage.getItem(localDraftCountKey);
    if (cachedDraftCount !== null) setDraftCount(+cachedDraftCount);

    const userVideosRef = collection(db, 'users', userId, 'workspaceContent');
    const userSettingsRef = doc(db, 'users', userId, 'userSettings', 'workspace');

    const unsubscribeWorkspaceContent = onSnapshot(
      userVideosRef,
      async snapshot => {
        if (!snapshot.empty) {
          const videosFromFirestore = [];
          const thumbnailPromises = [];
          snapshot.forEach(docSnap => {
            const data = docSnap.data();
            const id = docSnap.id.replace('workspace-', '');
            data.id = id;
            if (data.video_thumbnail?.startsWith('bumpThumbnails/')) {
              thumbnailPromises.push(
                (async () => {
                  try {
                    const url = await getDownloadURL(
                      ref(storage, data.video_thumbnail)
                    );
                    await setDoc(docSnap.ref, { video_thumbnail: url }, { merge: true });
                    data.video_thumbnail = url;
                  } catch {
                    data.video_thumbnail = placeholder_color_thumbnail;
                  }
                })()
              );
            }
            videosFromFirestore.push(data);
          });
          await Promise.all(thumbnailPromises);
          videosFromFirestore.sort(
            (a, b) =>
              b.workspace_creation_time.seconds -
              a.workspace_creation_time.seconds
          );
          setVideos(videosFromFirestore);
          try {
            localStorage.setItem(localVideoCacheKey, JSON.stringify(videosFromFirestore));
          } catch (e) {
            if (e.name.includes('Quota')) {
              localStorage.removeItem(localVideoCacheKey);
            }
          }
          setIsContentAvailable(true);
        } else {
          setVideos([]);
          localStorage.removeItem(localVideoCacheKey);
          setIsContentAvailable(false);
        }
        setIsLoading(false);
      },
      () => setIsLoading(false)
    );

    const unsubscribeUserSettings = onSnapshot(userSettingsRef, docSnap => {
      const count = docSnap.exists() ? docSnap.data().draft_count ?? 0 : 0;
      setDraftCount(count);
      localStorage.setItem(localDraftCountKey, count);
    });

    return () => {
      unsubscribeWorkspaceContent();
      unsubscribeUserSettings();
    };
  }, []);

  useEffect(() => {
    setIsLoading(true);
    const user = auth.currentUser;
    if (!user) {
      setIsLoading(false);
      return;
    }
    const cacheKey = getLocalVideoCacheKey(user.uid);
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      setVideos(JSON.parse(cached));
      setIsContentAvailable(true);
      setIsLoading(false);
    }
    const draftKey = getLocalDraftCountKey(user.uid);
    const draftCached = localStorage.getItem(draftKey);
    if (draftCached !== null) setDraftCount(+draftCached);

    const unsub = checkFirestore(user.uid);
    return () => unsub && unsub();
  }, [checkFirestore]);

  const handleVideoClick = id => navigate(`/workspace/${id}`);
  const handleCreateWorkspace = () => navigate('/workspace/new');

  const formatDate = ts => {
    if (!ts?.seconds) return '';
    const d = new Date(ts.seconds * 1000);
    return `${d.toLocaleString('default', { month: 'long' })} ${d.getDate()}, ${d.getFullYear()}`;
  };
  const formatDuration = sec => {
    if (sec == null || isNaN(sec)) return '--';
    const t = Math.floor(sec),
      h = Math.floor(t / 3600),
      m = Math.floor((t % 3600) / 60),
      s = t % 60;
    return h
      ? `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
      : `${m}:${s.toString().padStart(2, '0')}`;
  };

  const idxLast = currentPage * videosPerPage;
  const idxFirst = idxLast - videosPerPage;
  const currentVideos = videos.slice(idxFirst, idxLast);
  const totalPages = Math.ceil(videos.length / videosPerPage);
  const startNum = idxFirst + 1;
  const endNum = Math.min(idxLast, videos.length);

  const nextPage = () => setCurrentPage(p => Math.min(p + 1, totalPages));
  const prevPage = () => setCurrentPage(p => Math.max(p - 1, 1));

  const handleSelectToggle = id => {
    setSelectedVideos(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const someVisibleSelected = currentVideos.some(v => selectedVideos.has(v.id));

  return (
    <div className="listworkspace-video-container">
      <div className="listworkspace-header-row">
        <div className="listworkspace-header-item select-column-header">
          <input
            type="checkbox"
            className="listworkspace-checkbox"
            checked={someVisibleSelected}
            onChange={handleSelectAllVisible}
          />
        </div>

        <div className="listworkspace-header-item">
          Workspaces
          <span className="bulk-action-buttons">
            <button
              className="video-button"
              data-tooltip="+ Workspace" 
              onClick={handleCreateWorkspace}
              disabled={draftCount >= 10}
              aria-label={
                draftCount >= 10
                  ? 'Maximum number of drafts reached'
                  : '+ Workspace'
              }
            >
              <FontAwesomeIcon icon={faComment} />
            </button>

            {selectedVideos.size > 0 && (
              <>
                <button
                  className="video-button"
                  data-tooltip="Delete"
                  onClick={confirmDelete}
                  aria-label="Delete Selected"
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </>
            )}
          </span>
        </div>

        {(device === 'tablet' || device === 'desktop') && (
          <div className="listworkspace-header-item">Status</div>
        )}
        {device === 'desktop' && (
          <div className="listworkspace-header-item">Duration</div>
        )}
        {device !== 'mobile' && (
          <div className="create-workspace-button-container">
            <button
              className="create-workspace-button"
              onClick={handleCreateWorkspace}
              disabled={draftCount >= 10}
            >
              {draftCount >= 10 ? 'Max Drafts [10]' : '+ Workspace'}
            </button>
          </div>
        )}
      </div>

      {isLoading ? (
        <ProcessingPage />
      ) : isContentAvailable ? (
        <>
          {currentVideos.map((video, idx) => {
            const isHovered = hoveredIndex === idx;
            return (
              <div
                key={video.id}
                className="listworkspace-video-row"
                onMouseEnter={() => setHoveredIndex(idx)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                <div className="listworkspace-select-cell">
                  <input
                    type="checkbox"
                    checked={selectedVideos.has(video.id)}
                    onChange={() => handleSelectToggle(video.id)}
                    className="listworkspace-checkbox"
                  />
                </div>

                <div className="listworkspace-video-item">
                  <img
                    src={video.video_thumbnail || placeholder_color_thumbnail}
                    alt="thumbnail"
                    className="listworkspace-thumbnail"
                    onClick={() => handleVideoClick(video.id)}
                  />
                  <div className="listworkspace-title-button-wrapper">
                    <div
                      className="listworkspace-video-title clickable-title"
                      onClick={() => handleVideoClick(video.id)}
                    >
                      {video.workspace_title || video.video_file_name}
                    </div>
                    <div className="listworkspace-description-button-wrapper">
                      {isHovered ? (
                        <button
                          className="video-button"
                          onClick={() => handleVideoClick(video.id)}
                          aria-label="View Video"
                        >
                          <FontAwesomeIcon icon={faCog} />
                        </button>
                      ) : (
                        <div className="listworkspace-video-description">
                          {formatDate(video.workspace_creation_time)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="listworkspace-video-status">
                  {video.is_transcript_error ? (
                    <div className="error-badge">
                      <img
                        src={warning_icon}
                        alt="Failed Icon"
                        className="draft-icon"
                      />
                      <span className="draft-text">Upload Error</span>
                    </div>
                  ) : (video.is_transcript_available ||
                      video.is_local_transcript_available) ? (
                    <div className="listwork-active-badge">
                      <img
                        src={comment_icon}
                        alt="Active Icon"
                        className="active-icon-workspace"
                      />
                      <span className="active-text">Active</span>
                    </div>
                  ) : video.is_caption_processing ? (
                    <div className="badge badge-uploading">
                      <img
                        src={upload_small_icon}
                        alt="Uploading Icon"
                        className="draft-icon"
                      />
                      <span className="draft-text">Uploading</span>
                    </div>
                  ) : video.is_local_caption_processing ? (
                    <div className="badge badge-uploading">
                      <img
                        src={upload_small_icon}
                        alt="Processing Icon"
                        className="draft-icon"
                      />
                      <span className="draft-text">Processing</span>
                    </div>
                  ) : (
                    <div className="draft-badge">
                      <img
                        src={workspace_draft_icon}
                        alt="Draft Icon"
                        className="draft-icon"
                      />
                      <span className="draft-text">Draft</span>
                    </div>
                  )}
                </div>

                {device === 'desktop' && (
                  <div className="listworkspace-video-duration">
                    {formatDuration(video.video_duration_in_seconds)}
                  </div>
                )}
                <div className="listworkspace-empty-cell" />
              </div>
            );
          })}

          <div className="listworkspace-pagination-controls">
            <span className="listworkspace-pagination-info">
              {startNum}-{endNum} of about {videos.length}
            </span>
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="listworkspace-pagination-button"
            >
              <i className="listworkspace-outside-arrow listworkspace-double-left" />
            </button>
            <button
              onClick={prevPage}
              disabled={currentPage === 1}
              className="listworkspace-pagination-button"
            >
              <i className="listworkspace-arrow listworkspace-left" />
            </button>
            <button
              onClick={nextPage}
              disabled={currentPage === totalPages}
              className="listworkspace-pagination-button"
            >
              <i className="listworkspace-arrow listworkspace-right" />
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className="listworkspace-pagination-button"
            >
              <i className="listworkspace-outside-arrow listworkspace-double-right" />
            </button>
          </div>
        </>
      ) : (
        <div className="no-content-wrapper">
          <img
            src={placeholder_color_thumbnail}
            alt="No content"
            className="no-content-image"
          />
          <div className="no-content-message">Upload a video to get started</div>
          <button
            className="create-workspace-button"
            onClick={handleCreateWorkspace}
          >
            Create Workspace
          </button>
        </div>
      )}

      {showConfirm && (
        <div className="d-popup-overlay">
          <div className="d-popup-content">
            <h2>Are you sure you want to delete?</h2>
            <p>
              This action is irreversible and the workspace won’t be recoverable,
              so all outputs will be lost.
            </p>
            <div className="d-popup-buttons">
              <button className="d-popup-cancel" onClick={onCancelDeletion}>
                Cancel
              </button>
              <button className="d-popup-confirm" onClick={onConfirmDeletion}>
                Confirm Deletion
              </button>
            </div>
          </div>
        </div>
      )}

      {isBulkDeleting && (
        <div className="bulk-delete-overlay">
          <div className="bulk-delete-dialog">
            <div className="bulk-delete-spinner" />
            <div className="bulk-delete-text">Loading…</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListViewWorkspace;
